import clsx from "clsx";
import React from "react";
import { Box, Drawer, DrawerProps } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  NutritionTargetDto,
  NutritionTargetType,
  TargetInfo as ApiTargetInfo,
} from "@growth-machine-llc/stridist-api-client";
import TargetsView from "./TargetsView";
import TargetTypeInfo from "./TargetTypeInfo";
import { TargetInfo } from "./TargetsView";
import {
  getNutritionPair,
  getNutritionTargetTypes,
  NutritionTargetPairs,
} from "../../../constants";
import { NutritionHeader } from "../common/NutritionHeader";
import SelectTargetPair from "./SelectTargetPair";
import LoadingButton from "../../button/LoadingButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import NutritionTargetsService from "../../../services/NutritionTargetsService";
import {
  CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY,
  CLIENT_NUTRITION_TARGETS_QUERY_KEY,
} from "../ClientNutritionCard";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import {
  DeleteConfirmationMode,
  useDeleteConfirmation,
} from "../../program/modals/DeleteConfirmation.tsx/DeleteConfirmationModalContext";
import DeleteConfirmationTooltip from "../../program/modals/DeleteConfirmation.tsx/DeleteConfirmationTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDrawer-paper" : {
      zIndex: theme.zIndex.nutritionDrawer,
      
      // margin for IOS devices
      marginTop: `calc(max(var(--safe-area-inset-top) - ${theme.spacing(2)}, 0px))`,
    }
  },

  header: {
    marginBottom: theme.spacing(2),
  },

  typeInfo: {
    marginBottom: theme.spacing(2),
  },

  paper: {
    padding: theme.spacing(5, 4),
    width: "100%",

    [theme.breakpoints.up("md")]: {
      width: theme.spacing(58),
      boxShadow: theme.shadows[8],
    },

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(5, 2),
    },
  },

  headerPadding: {
    [theme.breakpoints.down("sm")]: {
      paddingInline: theme.spacing(2),
    },
  },

  editContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },

  button: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
    padding: theme.spacing(1.5),
    width: "100%",
    marginTop: theme.spacing(2),
  },
}));

export enum ClientNutritionTargetDrawerView {
  PAIRS = "PAIRS",
  EDIT = "EDIT",
}

export interface ClientNutritionTargetDrawerProps extends DrawerProps {
  nutritionTargets: NutritionTargetDto[];
  clientInfo: { clientId?: number; username?: string };
}

export function NewDrawer(props: ClientNutritionTargetDrawerProps) {
  const {
    className,
    clientInfo,
    onClose,
    open,
    nutritionTargets: allTargets,
  } = props;

  const nutritionTargets = React.useMemo(
    () => allTargets.filter((t) => !t.expiredAt),
    [allTargets],
  );

  const clientHasTarget = nutritionTargets.length > 0;

  const s = useStyles();
  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();
  const { clientId, username } = clientInfo;
  const existingTypes = nutritionTargets.map((target) => target.targetType);
  const existingPair = getNutritionPair(existingTypes);
  const [pair, setPair] = React.useState<NutritionTargetPairs>(
    existingPair || NutritionTargetPairs.ALL,
  );
  const [view, setView] = React.useState<ClientNutritionTargetDrawerView>(
    clientHasTarget
      ? ClientNutritionTargetDrawerView.EDIT
      : ClientNutritionTargetDrawerView.PAIRS,
  );

  const getStateForNewTargetTypes = (
    targetTypes: NutritionTargetType[],
  ): Record<NutritionTargetType, TargetInfo> => {
    return targetTypes.reduce(
      (acc, type) => {
        acc[type] = {
          protein: { value: 0, dirty: false },
          carbohydrate: { value: 0, dirty: false },
          fat: { value: 0, dirty: false },
          calories: { value: 0, dirty: false },
        };
        return acc;
      },
      {} as Record<NutritionTargetType, TargetInfo>,
    );
  };

  const getStateForExistingTargetTypes = (
    nutritionTargets: NutritionTargetDto[],
  ): Record<NutritionTargetType, TargetInfo> => {
    return nutritionTargets.reduce(
      (acc, target) => {
        acc[target.targetType] = {
          protein: { value: target.protein, dirty: false },
          carbohydrate: { value: target.carbs, dirty: false },
          fat: { value: target.fat, dirty: false },
          calories: { value: target.calories, dirty: false },
        };
        return acc;
      },
      {} as Record<NutritionTargetType, TargetInfo>,
    );
  };

  const getDefaultState = (targetsPiar: NutritionTargetPairs) =>
    nutritionTargets.length === 0
      ? getStateForNewTargetTypes(getNutritionTargetTypes(targetsPiar))
      : getStateForExistingTargetTypes(nutritionTargets);

  const [state, setState] = React.useState<
    Record<NutritionTargetType, TargetInfo>
  >(getDefaultState(pair));

  React.useEffect(() => {
    if (nutritionTargets.length > 0) {
      setState(getDefaultState(NutritionTargetPairs.ALL));
    }
  }, [nutritionTargets]);

  const { mutate: removeTargetsMutation, isPending: removingTargets } =
    useMutation({
      mutationKey: ["remove-nutrition-targets"],
      mutationFn: NutritionTargetsService.removeNutritionTargets,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_NUTRITION_TARGETS_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY],
        });
        setPair(NutritionTargetPairs.ALL);
        setState(
          getStateForNewTargetTypes(
            getNutritionTargetTypes(NutritionTargetPairs.ALL),
          ),
        );
        showToastAlert("success", {
          message: `Nutrition target removed`,
        });
      },
    });

  const { mutate: setTargetsMutation, isPending: settingTargets } = useMutation(
    {
      mutationKey: ["set-nutrition-targets"],
      mutationFn: NutritionTargetsService.setNutritionTargets,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_NUTRITION_TARGETS_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY],
        });
        showToastAlert("success", {
          message: `Nutrition target updated`,
        });
      },
    },
  );

  const { openDialog: openDeleteConfirmationDialog, popupState } =
    useDeleteConfirmation();

  const handleSwitchTargetPairTypes = (newPair: NutritionTargetPairs) => {
    setView(ClientNutritionTargetDrawerView.EDIT);
    setPair(newPair);

    // new sorted by type
    const emptyTargets = Object.entries(
      getStateForNewTargetTypes(getNutritionTargetTypes(newPair)),
    ).sort(([keyA], [keyB]) => {
      const indexA = Object.keys(getNutritionTargetTypes(newPair)).indexOf(
        keyA,
      );
      const indexB = Object.keys(getNutritionTargetTypes(newPair)).indexOf(
        keyB,
      );
      return indexA - indexB;
    });

    // current sorted by type
    const currentTargets = Object.entries(state).sort(([keyA], [keyB]) => {
      const indexA = Object.keys(getNutritionTargetTypes(pair)).indexOf(keyA);
      const indexB = Object.keys(getNutritionTargetTypes(pair)).indexOf(keyB);
      return indexA - indexB;
    });

    // fill empty targets with current targets (if iteration present)
    for (let i = 0; i < emptyTargets.length; i++) {
      if (currentTargets?.[i]) {
        const [key, __] = emptyTargets[i];
        const [_, currentValue] = currentTargets[i];
        emptyTargets[i][1] = { ...currentValue };
      }
    }

    setState(
      emptyTargets.reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<NutritionTargetType, TargetInfo>,
      ),
    );
  };

  const handleClose = () => {
    onClose({}, "backdropClick");
    if (clientHasTarget) {
      setView(ClientNutritionTargetDrawerView.EDIT);
    }
    setState(getDefaultState(existingPair || NutritionTargetPairs.ALL));
    setPair(existingPair || NutritionTargetPairs.ALL);
  };

  const handleRemove = (e: React.MouseEvent<HTMLElement>) => {
    onClose(e, "backdropClick");
    removeTargetsMutation({ username });
  };

  const handleRemoveClick = (e: React.MouseEvent<HTMLElement>) => {
    openDeleteConfirmationDialog(DeleteConfirmationMode.NUTRITION_TARGET, () =>
      handleRemove(e),
    );
    popupState.open(e);
  };

  const handleSave = () => {
    const targets = Object.entries(state).map(
      ([key, value]) =>
        ({
          type: key,
          protein: value.protein.value,
          carbs: value.carbohydrate.value,
          fat: value.fat.value,
          calories: value.calories.value,
        }) as unknown as ApiTargetInfo,
    );
    onClose({}, "backdropClick");
    setTargetsMutation({ username, targets });
    setView(ClientNutritionTargetDrawerView.EDIT);
  };

  const setPairSelectionView = () => {
    setView(ClientNutritionTargetDrawerView.PAIRS);
  };

  const handleCancelPairChange = () => {
    if (clientHasTarget) {
      setView(ClientNutritionTargetDrawerView.EDIT);
    } else {
      onClose({}, "backdropClick");
    }
    setState(getDefaultState(existingPair || NutritionTargetPairs.ALL));
    setPair(existingPair || NutritionTargetPairs.ALL);
  };

  const isPending = removingTargets || settingTargets;

  return (
    <Drawer
      className={clsx(s.root, className)}
      classes={{
        paper: s.paper,
      }}
      anchor="right"
      variant="persistent"
      onClose={handleClose}
      open={open}
      children={
        <>
          <DeleteConfirmationTooltip />
          <Box className={s.headerPadding}>
            <NutritionHeader
              className={s.header}
              onClose={handleClose}
              onDelete={
                existingTypes.length > 0 ? handleRemoveClick : undefined
              }
              title={"Nutrition Targets"}
            />
          </Box>

          {view === ClientNutritionTargetDrawerView.EDIT ? (
            <Box className={s.editContainer}>
              <Box>
                <TargetTypeInfo
                  className={s.typeInfo}
                  pair={pair}
                  onChangeSchedule={setPairSelectionView}
                />
                <TargetsView state={state} setState={setState} />
              </Box>
              <LoadingButton
                className={s.button}
                variant="contained"
                children="Save targets"
                onClick={handleSave}
                disabled={isPending}
                loading={isPending}
              />
            </Box>
          ) : (
            <SelectTargetPair
              pair={pair}
              onChangePair={handleSwitchTargetPairTypes}
              onCancel={handleCancelPairChange}
              currentlyUsedPair={existingPair}
              buttonLabel={clientHasTarget ? "Change schedule" : "Set schedule"}
            />
          )}
        </>
      }
    />
  );
}
